import { apiCustomer } from "@/api"
import { Button, Card, CloseButton, Heading, Icon, IconBadge, Spinner, StaggerGroup, StaggerItem, Surface, Text } from "@/components/ui";
import { SCRIM } from "@/components/ui/Scrim"
import { useModalLayer } from "@/contexts/ModalContext"
import { cn } from "@/lib/cn"
import { DURATION, SPRING, STAGGER, dialogVariants } from "@/lib/motion"
import { Clock, Confetti, PixIcon } from "@/icons"
import { getOpenPixCharges } from "@/lib/order-status"
import type { OrderResponse } from "@/types/external"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { QRCodeSVG } from "qrcode.react"

const MotionSurface = motion.create(Surface)

export type PixQrOverlayStatus =
  | "awaiting"
  | "expired"
  | "reconciling"
  | "confirmed"
  | "reconcileFailed"

/**
 * How the overlay decides the customer has paid:
 * - `saga` (default): POST /orders/:id/reconcile-payment — original checkout
 *   PIX_ONLINE validation saga. Must NOT be used for mid-order ad-hoc charges.
 * - `openCharge`: GET /orders/:id and treat as confirmed when `chargeId` is no
 *   longer in `openCharges` (or there are no open charges). The checkout
 *   reconcile RPC only knows about the original payment and returns
 *   "confirmed" for an already-paid order even while the difference charge is
 *   still open — that was the false-positive "paid" UI with the card still up.
 */
export type PixQrVerifyMode = "saga" | "openCharge"

type Props = {
  open: boolean
  orderId: string
  qrCode: string
  qrCodeBase64: string
  expiresAt: string
  onConfirmed: () => void
  /** Dismiss the overlay via the top-right X. Caller decides where to go next. */
  onClose?: () => void
  /** Payment verification strategy. Defaults to checkout saga reconcile. */
  verifyMode?: PixQrVerifyMode
  /** Required when verifyMode is `openCharge` — the chargeId on openCharges. */
  chargeId?: string
}

function useCountdown(expiresAt: string): { minutes: number; seconds: number; expired: boolean } {
  const [remaining, setRemaining] = useState(() => {
    const ms = new Date(expiresAt).getTime() - Date.now()
    return Math.max(0, Math.floor(ms / 1000))
  })

  useEffect(() => {
    if (remaining <= 0) return

    const id = setInterval(() => {
      const ms = new Date(expiresAt).getTime() - Date.now()
      const secs = Math.max(0, Math.floor(ms / 1000))
      setRemaining(secs)
    }, 1000)

    return () => clearInterval(id)
  }, [expiresAt, remaining])

  return {
    minutes: Math.floor(remaining / 60),
    seconds: remaining % 60,
    expired: remaining <= 0,
  }
}

/** True when the ad-hoc charge is no longer open on the order snapshot. */
function isOpenChargeSettled(
  order: OrderResponse | null | undefined,
  chargeId: string | undefined,
): boolean {
  const open = getOpenPixCharges(order)
  if (!chargeId) return open.length === 0
  return !open.some((c) => c.chargeId === chargeId)
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
  }, [])

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
      resetTimerRef.current = setTimeout(() => setCopied(false), 2000)
    } catch {
    }
  }

  return (
    <Button size="lg" fullWidth onClick={handleCopy} className="mt-3 whitespace-nowrap">
      {copied ? "Copiado!" : "Copiar código Pix"}
    </Button>
  )
}

type ReconcileResponse = {
  status?: string
  outcome?: string
}

export function PixQrOverlay({
  open,
  orderId,
  qrCode,
  expiresAt,
  onConfirmed,
  onClose,
  verifyMode = "saga",
  chargeId,
}: Props) {
  const countdown = useCountdown(expiresAt)
  const reduceMotion = useReducedMotion()
  const { zIndex } = useModalLayer(open)
  const [overlayStatus, setOverlayStatus] = useState<PixQrOverlayStatus>("awaiting")
  const [reconcileError, setReconcileError] = useState("")
  const [revealed, setRevealed] = useState(false)
  const confirmedRef = useRef(false)
  const revertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [reconcileVisible, setReconcileVisible] = useState(false)

  useEffect(() => {
    if (!open) return
    setOverlayStatus("awaiting")
    setReconcileError("")
    confirmedRef.current = false
    setReconcileVisible(false)
    const reconcileDelay = setTimeout(() => setReconcileVisible(true), 45000)
    return () => {
      clearTimeout(reconcileDelay)
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current)
    }
  }, [open])

  useEffect(() => {
    if (!open || verifyMode !== "openCharge" || confirmedRef.current) return

    let cancelled = false
    async function pollOpenCharge() {
      try {
        const { data } = await apiCustomer.get<OrderResponse>(`/orders/${orderId}`)
        if (cancelled || confirmedRef.current) return
        if (isOpenChargeSettled(data, chargeId)) {
          confirmedRef.current = true
          setOverlayStatus("confirmed")
          setTimeout(() => onConfirmed(), 1200)
        }
      } catch {
      }
    }

    void pollOpenCharge()
    const id = setInterval(pollOpenCharge, 3000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [open, verifyMode, orderId, chargeId, onConfirmed])

  useEffect(() => {
    if (!open) {
      setRevealed(false)
      return
    }
    if (reduceMotion) {
      setRevealed(true)
      return
    }
    setRevealed(false)
    const id = setTimeout(() => setRevealed(true), 280)
    return () => clearTimeout(id)
  }, [open, reduceMotion])

  useEffect(() => {
    if (overlayStatus === "awaiting" && countdown.expired) {
      setOverlayStatus("expired")
    }
  }, [countdown.expired, overlayStatus])

  function scheduleReconcileReset() {
    if (revertTimerRef.current) clearTimeout(revertTimerRef.current)
    revertTimerRef.current = setTimeout(() => {
      setReconcileError("")
      setOverlayStatus(countdown.expired ? "expired" : "awaiting")
    }, 4000)
  }

  async function handleReconcile() {
    if (overlayStatus === "reconciling") return
    if (revertTimerRef.current) clearTimeout(revertTimerRef.current)
    setOverlayStatus("reconciling")
    setReconcileError("")

    try {
      if (verifyMode === "openCharge") {
        const { data } = await apiCustomer.get<OrderResponse>(`/orders/${orderId}`)
        if (isOpenChargeSettled(data, chargeId)) {
          setOverlayStatus("confirmed")
          confirmedRef.current = true
          setTimeout(() => onConfirmed(), 1200)
        } else {
          setOverlayStatus("reconcileFailed")
          setReconcileError(
            "O pagamento ainda não foi identificado. Aguarde alguns instantes e tente de novo.",
          )
          scheduleReconcileReset()
        }
        return
      }

      const { data } = await apiCustomer.post<ReconcileResponse>(`/orders/${orderId}/reconcile-payment`)
      const outcome = (data?.status ?? data?.outcome ?? "").toLowerCase()

      if (outcome === "confirmed") {
        setOverlayStatus("confirmed")
        confirmedRef.current = true
        setTimeout(() => onConfirmed(), 1200)
      } else {
        setOverlayStatus("reconcileFailed")
        setReconcileError(
          "O pagamento ainda não foi identificado. Aguarde alguns instantes e tente de novo.",
        )
        scheduleReconcileReset()
      }
    } catch {
      setOverlayStatus("reconcileFailed")
      setReconcileError("Não conseguimos verificar o pagamento agora. Tente novamente em instantes.")
      scheduleReconcileReset()
    }
  }

  const isLoading = overlayStatus === "reconciling"
  const isConfirmed = overlayStatus === "confirmed"
  const isExpired = !isConfirmed && (countdown.expired || overlayStatus === "expired")
  const showLiveQr = !isConfirmed && !isExpired && !!qrCode

  const title = isConfirmed
    ? "Pagamento confirmado!"
    : isExpired
      ? "QR Code expirado"
      : "Pague com Pix"

  const subtitle = isConfirmed
    ? "Seu pedido foi recebido e já está sendo preparado."
    : isExpired
      ? "O tempo para pagar com este código acabou. Este QR não é mais válido."
      : "Escaneie o QR Code ou copie o código para pagar."

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="alertdialog"
          aria-modal="true"
          aria-label={isExpired ? "QR Code Pix expirado" : "Pagamento via Pix"}
          style={{ zIndex }}
          className={cn("fixed inset-0 grid place-items-center p-4", SCRIM)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURATION.moderate }}
        >
          <MotionSurface
            surface="card"
            layout
            className={cn(
              "relative flex max-h-sheet w-full flex-col items-center overflow-y-auto rounded-sheet px-6 py-8 text-center shadow-overlay",
              revealed ? "max-w-md" : "max-w-xs",
            )}
            variants={dialogVariants(reduceMotion)}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ layout: SPRING.dialog }}
          >
            {onClose && (
              <CloseButton onClick={onClose} className="absolute right-3 top-3 z-raised" />
            )}

            <div className="flex flex-col items-center gap-2 text-center">
              <Icon as={PixIcon} size="3xl" tone={isExpired ? "danger" : "primary"} />
              <Heading level={1}>{title}</Heading>
              <Text size="base" tone="muted" aria-live="polite">
                {subtitle}
              </Text>
            </div>

            <AnimatePresence>
              {revealed && (
                <StaggerGroup
                  step="base"
                  delay={STAGGER.tight}
                  className="mt-6 flex w-full flex-col items-center gap-6"
                >
                  {showLiveQr && (
                    <StaggerItem className="flex w-full flex-col items-center gap-4">
                      <div className="rounded-card border-2 border-border bg-on-media p-4 shadow-card">
                        <QRCodeSVG value={qrCode} size={220} level="M" aria-label="QR Code Pix" />
                      </div>

                      <Text
                        as="div"
                        size="base"
                        weight="semibold"
                        tone={countdown.minutes === 0 && countdown.seconds <= 60 ? "danger" : "muted"}
                        className="flex items-center gap-1.5"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        <span>Expira em</span>
                        <span>
                          {countdown.minutes}:{countdown.seconds.toString().padStart(2, "0")}
                        </span>
                      </Text>

                      <div className="w-full">
                        <Text size="sm" weight="semibold" tone="muted" align="left" className="mb-1">
                          Pix Copia e Cola
                        </Text>
                        <Card variant="tinted" padding="sm">
                          <Text as="div" size="xs" tone="muted" align="left" className="break-all select-all font-mono">
                            {qrCode}
                          </Text>
                        </Card>
                        <CopyButton text={qrCode} />
                      </div>
                    </StaggerItem>
                  )}

                  {isExpired && (
                    <StaggerItem
                      className="flex w-full flex-col items-center gap-4"
                      role="status"
                      aria-label="QR Code expirado"
                    >
                      <Card
                        variant="dashed"
                        tone="danger"
                        padding="2xl"
                        className="flex w-full flex-col items-center justify-center gap-3"
                      >
                        <Icon as={Clock} size="4xl" tone="danger" weight="bold" />
                        <Text size="lg" weight="bold" tone="danger">QR Code expirado</Text>
                        <Text size="sm" tone="muted" className="max-w-xs">
                          Este código não pode mais ser usado para pagar. Se o valor já saiu da
                          sua conta, verifique o pagamento abaixo.
                        </Text>
                      </Card>

                      {isLoading ? (
                        <Text as="div" tone="muted" className="flex items-center justify-center gap-2">
                          <Spinner tone="primary" />
                          <span>Verificando pagamento...</span>
                        </Text>
                      ) : (
                        <Button type="button" onClick={handleReconcile} disabled={isLoading} fullWidth>
                          Já paguei, verificar pagamento
                        </Button>
                      )}

                      <Text size="sm" tone="muted">
                        Ainda não pagou?{" "}
                        {onClose ? (
                          <>
                            <Button variant="link" size="auto" weight="semibold" onClick={onClose}>
                              Feche e faça um novo pedido
                            </Button>{" "}
                            para gerar um QR Code novo.
                          </>
                        ) : (
                          "Feche esta tela e faça um novo pedido para gerar um QR Code novo."
                        )}
                      </Text>
                    </StaggerItem>
                  )}

                  {isConfirmed && (
                    <StaggerItem className="flex flex-col items-center gap-3 text-center">
                      <IconBadge icon={Confetti} tone="success" size="xl" weight="duotone" />
                      <Text size="base" tone="muted">
                        A loja já recebeu seu pedido e vai começar o preparo.
                      </Text>
                    </StaggerItem>
                  )}

                  <AnimatePresence>
                    {reconcileError && overlayStatus === "reconcileFailed" && (
                      <motion.div
                        key="reconcile-error"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: DURATION.moderate }}
                      >
                        <Text size="xs" weight="medium" tone="danger" align="center">
                          {reconcileError}
                        </Text>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {!isConfirmed && !isExpired && reconcileVisible && (
                    <StaggerItem className="flex w-full flex-col items-center gap-3">
                      {isLoading ? (
                        <Text as="div" tone="muted" className="flex items-center justify-center gap-2">
                          <Spinner tone="primary" />
                          <span>Verificando pagamento...</span>
                        </Text>
                      ) : (
                        <Text as="div" size="sm">
                          <Button variant="link" size="auto" weight="semibold" onClick={handleReconcile}>
                            <Text as="span" size="sm">
                              Realizei o pagamento e o pedido não aprovou
                            </Text>
                          </Button>
                        </Text>
                      )}
                    </StaggerItem>
                  )}

                  {isConfirmed && (
                    <StaggerItem className="w-full">
                      <Button type="button" onClick={onConfirmed} fullWidth>
                        Acompanhar pedido
                      </Button>
                    </StaggerItem>
                  )}
                </StaggerGroup>
              )}
            </AnimatePresence>
          </MotionSurface>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
